import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from './config';
import { FUNCTIONS_REGION } from './region';
import type { Anchor } from '../quran/anchor';

export type Track = {
  id: string;
  name: string;
  surahs: number[];
  totalAyahs: number;
};

export type Standing = {
  studentId: string;
  displayName: string;
  ayahs: number;
  total: number;
  percent: number;
  errors: number;
};

/** What one student has covered, per surah, as merged ranges. */
/*
 * Re-exported, never redeclared. This file used to carry its own copy of the
 * shape with `ranges` as pairs, which is precisely how the stored form and
 * the read form came to disagree.
 */
import type { AyahRange, SurahCoverage } from '../quran/trackProgress';

export type { AyahRange, SurahCoverage };

function toTrack(id: string, data: Record<string, unknown>): Track {
  const surahs = Array.isArray(data.surahs) ? data.surahs.map(Number).filter(Number.isInteger) : [];
  return {
    id,
    name: String(data.name ?? ''),
    surahs,
    totalAyahs: typeof data.totalAyahs === 'number' ? data.totalAyahs : 0,
  };
}

/** Every track, live: an admin editing one is seen by the students browsing. */
export function listenToTracks(
  cb: (tracks: Track[]) => void,
  onError?: (e: Error) => void
): () => void {
  return onSnapshot(
    query(collection(db, 'tracks'), orderBy('name')),
    (snap) => cb(snap.docs.map((d) => toTrack(d.id, d.data() as Record<string, unknown>))),
    (e) => onError?.(e)
  );
}

/**
 * The board for one track.
 *
 * Ordered by how much of the track has been covered. Ties keep their order
 * from Firestore rather than being broken by errors — a student is not ranked
 * below another for having been corrected more.
 */
export function listenToStandings(
  trackId: string,
  cb: (standings: Standing[]) => void,
  onError?: (e: Error) => void
): () => void {
  return onSnapshot(
    query(collection(db, 'tracks', trackId, 'standings'), orderBy('ayahs', 'desc')),
    (snap) => cb(snap.docs.map((d) => {
      const data = d.data() as Record<string, unknown>;
      return {
        studentId: d.id,
        displayName: String(data.displayName ?? ''),
        ayahs: Number(data.ayahs ?? 0),
        total: Number(data.total ?? 0),
        percent: Number(data.percent ?? 0),
        errors: Number(data.errors ?? 0),
      };
    })),
    (e) => onError?.(e)
  );
}

/** The tracks this student has joined. Their own document, one read. */
export function useMyTrackIds(uid: string | null): { trackIds: string[]; loading: boolean } {
  const [state, setState] = useState<{ trackIds: string[]; loading: boolean }>({
    trackIds: [], loading: true,
  });
  useEffect(() => {
    setState({ trackIds: [], loading: true });
    if (!uid) { setState({ trackIds: [], loading: false }); return undefined; }
    return onSnapshot(
      doc(db, 'trackMembers', uid),
      (snap) => {
        const held = snap.data()?.trackIds;
        setState({ trackIds: Array.isArray(held) ? held.map(String) : [], loading: false });
      },
      // Fail closed: showing nothing joined is recoverable, claiming a
      // membership that is not there is not.
      () => setState({ trackIds: [], loading: false })
    );
  }, [uid]);
  return state;
}

/** What this student has covered. Only ever their own, or their معلم's view. */
export function useCoverage(uid: string | null): Record<string, SurahCoverage> {
  const [surahs, setSurahs] = useState<Record<string, SurahCoverage>>({});
  useEffect(() => {
    setSurahs({});
    if (!uid) return undefined;
    return onSnapshot(
      doc(db, 'coverage', uid),
      (snap) => setSurahs((snap.data()?.surahs ?? {}) as Record<string, SurahCoverage>),
      () => setSurahs({})
    );
  }, [uid]);
  return surahs;
}

const call = <Req, Res>(name: string) =>
  httpsCallable<Req, Res>(getFunctions(app, FUNCTIONS_REGION), name);

/** Admin only; the rules refuse every client write to a track. */
export async function saveTrack(
  track: { id?: string; name: string; surahs: number[] }
): Promise<string> {
  const res = await call<typeof track, { id: string }>('saveTrack')(track);
  return res.data.id;
}

/** The student's own decision, in both directions. */
export async function joinTrack(trackId: string, leave = false): Promise<boolean> {
  const res = await call<{ trackId: string; leave: boolean }, { joined: boolean }>('joinTrack')(
    { trackId, leave }
  );
  return res.data.joined;
}

/**
 * One mistake, at one word.
 *
 * The server decides WHOSE mistake it is, from its own currentReciterId — the
 * caller says only where.
 */
export async function markError(sessionId: string, position: Anchor): Promise<void> {
  await call<{ sessionId: string; position: Anchor }, { ok: boolean }>('markError')(
    { sessionId, position }
  );
}
